import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "./ProductCard";

const TABS = [
  { id: "bestseller", label: "Best Sellers" },
  { id: "on-sale", label: "On Sale" },
];

export const FeaturedCarousel = () => {
  const [tab, setTab] = useState("bestseller");
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (cache[tab]) return;
    setLoading(true);
    api
      .get("/products", { params: { tag: tab, limit: 8 } })
      .then(({ data }) => setCache((c) => ({ ...c, [tab]: data })))
      .catch(() => setCache((c) => ({ ...c, [tab]: [] })))
      .finally(() => setLoading(false));
  }, [tab, cache]);

  const items = cache[tab] || [];

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = el.querySelector("[data-carousel-item]")?.getBoundingClientRect().width || 320;
    el.scrollBy({ left: dir * (cardW + 32), behavior: "smooth" });
  };

  return (
    <section data-testid="featured-carousel-section" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-28 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="label-track text-[#C8A165] mb-4">Handpicked</p>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A]">
              Featured Collection
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`featured-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 label-track transition-all border ${
                  tab === t.id
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <button
            data-testid="carousel-prev"
            onClick={() => scroll(-1)}
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] hover:text-[#C8A165] transition-all shadow-[0_4px_16px_-6px_rgba(0,0,0,0.15)]"
            aria-label="Previous"
          >
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button
            data-testid="carousel-next"
            onClick={() => scroll(1)}
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] hover:text-[#C8A165] transition-all shadow-[0_4px_16px_-6px_rgba(0,0,0,0.15)]"
            aria-label="Next"
          >
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>

          <div
            ref={scrollerRef}
            data-testid="carousel-scroller"
            className="flex gap-8 overflow-x-auto pb-6 snap-x-luxury scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loading && items.length === 0 && (
              <div className="text-[#737373] text-sm py-24 w-full text-center">Curating pieces…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-[#737373] text-sm py-24 w-full text-center">
                No pieces tagged yet. visit the Studio to feature products.
              </div>
            )}
            {items.map((p, i) => (
              <div
                key={p.id}
                data-carousel-item
                className="min-w-[260px] sm:min-w-[300px] lg:min-w-[320px] max-w-[320px]"
              >
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
